import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import { notesAPI } from '../../api/notesApi';
import NotesEditor from '../../components/editor/NotesEditor';
import {
    PhotoIcon,
    UserPlusIcon,
    XMarkIcon,
    MagnifyingGlassIcon,
    BriefcaseIcon,
    ChatBubbleLeftEllipsisIcon,
    PaperAirplaneIcon,
    TrashIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { usersAPI } from '../../api/api';
import Loading from '../../components/ui/Loading';
import NotesHome from './NotesHome';
import { useNotesActions } from './NotesActionsContext';
import { isNotesAdmin } from '../../constants/notes';

const NotesPage = () => {
    const { noteId } = useParams();
    const [note, setNote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saveStatus, setSaveStatus] = useState('saved'); // saved, saving, error
    const [importingWord, setImportingWord] = useState(false);
    const [exportingWord, setExportingWord] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [allUsers, setAllUsers] = useState([]);
    const [userSearchQuery, setUserSearchQuery] = useState('');
    const [sharingLoading, setSharingLoading] = useState(false);
    const [comments, setComments] = useState([]);
    const [commentText, setCommentText] = useState('');
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [sendingComment, setSendingComment] = useState(false);
    const [editorKey, setEditorKey] = useState(0);
    const [selectionAction, setSelectionAction] = useState({
        visible: false,
        text: '',
        x: 0,
        y: 0,
    });
    const pageContainerRef = useRef(null);
    const fileInputRef = useRef(null);
    const noteIdRef = useRef(noteId);
    const loadGenRef = useRef(0);
    const loadAbortRef = useRef(null);
    const commentsGenRef = useRef(0);
    const saveDebounceRef = useRef(null);
    const pendingSaveRef = useRef(null);
    const { user: currentUser } = useAuth();
    const { setActionsState } = useNotesActions();
    const isOwner = note?.owner === currentUser?.id;

    useEffect(() => {
        if (saveDebounceRef.current && pendingSaveRef.current) {
            const prevId = noteIdRef.current;
            const pendingData = pendingSaveRef.current;
            clearTimeout(saveDebounceRef.current);
            saveDebounceRef.current = null;
            pendingSaveRef.current = null;
            if (prevId) {
                notesAPI.updateNote(prevId, pendingData).catch(err =>
                    console.error('Flush save on navigate failed', err)
                );
            }
        }
        noteIdRef.current = noteId;
    }, [noteId]);

    // Word dosyası import
    const handleWordImport = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setImportingWord(true);
        try {
            const result = await notesAPI.importWord(file);
            if (result.success && result.blocks) {
                // Not içeriğini güncelle
                const newContent = { blocks: result.blocks };
                setNote(prev => ({ ...prev, content: newContent }));
                await notesAPI.updateNote(noteIdRef.current, { content: newContent });
                setSaveStatus('saved');
                setEditorKey(k => k + 1);
            } else {
                alert('Word dosyası işlenirken hata oluştu: ' + (result.error || 'Bilinmeyen hata'));
            }
        } catch (error) {
            console.error('Word import failed', error);
            alert('Word dosyası yüklenemedi');
        } finally {
            setImportingWord(false);
            event.target.value = ''; // Reset input
        }
    };

    const handleWordExport = async () => {
        if (!noteIdRef.current) return;
        setExportingWord(true);
        try {
            await notesAPI.exportWord(noteIdRef.current);
        } catch (error) {
            console.error('Word export failed', error);
            alert('Word dosyası indirilemedi');
        } finally {
            setExportingWord(false);
        }
    };

    const handleCreateTicket = async (selectedText = '') => {
        if (!noteIdRef.current) return;
        const selection = (selectedText || window.getSelection().toString()).trim();
        let message = 'Bu notu tamamen bir talebe (ticket) dönüştürmek istediğinize emin misiniz?';
        let requestData = {};

        if (selection) {
            message = 'Seçili metinden bir talep (ticket) oluşturmak istediğinize emin misiniz?';
            requestData = { selected_text: selection };
        }

        if (!window.confirm(message)) return;

        try {
            const res = await notesAPI.createTicketFromNote(noteIdRef.current, requestData);
            if (res.success) {
                alert('Talep başarıyla oluşturuldu! Talep no: ' + res.ticket_id);
            } else {
                alert('Hata: ' + (res.error || 'Bilinmeyen bir hata oluştu.'));
            }
        } catch (err) {
            console.error(err);
            alert('Talep oluşturulurken bir hata meydana geldi. ' + (err.error || ''));
        }
    };

    const openShareModal = async () => {
        setIsShareModalOpen(true);
        if (allUsers.length > 0) return;

        try {
            const response = await usersAPI.getUsers();
            let uData = [];
            if (Array.isArray(response)) {
                uData = response;
            } else if (response?.data && Array.isArray(response.data)) {
                uData = response.data;
            } else if (response?.results && Array.isArray(response.results)) {
                uData = response.results;
            }
            setAllUsers(uData.filter(u => u.id !== currentUser.id));
        } catch (e) {
            console.error('Users load failed', e);
        }
    };

    // İçerik değişiminde yeniden render olmasın diye ref kullanabiliriz
    // Ancak sayfa değiştiğinde (noteId) state resetlenmeli

    useEffect(() => {
        if (noteId) {
            loadNote(noteId);
            loadUsersForMentions();
            loadComments(noteId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [noteId]);

    const loadComments = async (id) => {
        const gen = ++commentsGenRef.current;
        setCommentsLoading(true);
        try {
            const response = await notesAPI.getComments(id);
            if (gen !== commentsGenRef.current) return;
            const list = Array.isArray(response) ? response : (response?.data || response?.results || []);
            setComments(Array.isArray(list) ? list : []);
        } catch (e) {
            console.error('Comments load failed', e);
            if (gen === commentsGenRef.current) setComments([]);
        } finally {
            if (gen === commentsGenRef.current) setCommentsLoading(false);
        }
    };

    const handleAddComment = async (e) => {
        e.preventDefault();
        const text = commentText.trim();
        if (!text || !noteIdRef.current) return;

        setSendingComment(true);
        try {
            const created = await notesAPI.createComment(noteIdRef.current, text);
            setComments(prev => [...prev, created]);
            setCommentText('');
        } catch (e) {
            console.error('Comment create failed', e);
            alert('Yorum eklenemedi.');
        } finally {
            setSendingComment(false);
        }
    };

    const handleDeleteComment = async (commentId) => {
        if (!window.confirm('Bu yorumu silmek istiyor musunuz?')) return;
        try {
            await notesAPI.deleteComment(noteIdRef.current, commentId);
            setComments(prev => prev.filter(c => c.id !== commentId));
        } catch (e) {
            console.error('Comment delete failed', e);
            alert('Yorum silinemedi.');
        }
    };

    const formatCommentDate = (value) => {
        if (!value) return '';
        try {
            return new Date(value).toLocaleString('tr-TR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return value;
        }
    };

    const loadUsersForMentions = async () => {
        if (allUsers.length > 0) return;
        try {
            const response = await usersAPI.getUsers();
            let uData = [];
            if (Array.isArray(response)) {
                uData = response;
            } else if (response?.data && Array.isArray(response.data)) {
                uData = response.data;
            } else if (response?.results && Array.isArray(response.results)) {
                uData = response.results;
            }
            setAllUsers(uData.filter(u => u.id !== currentUser?.id));
        } catch (e) {
            console.error('Users load for mentions failed', e);
        }
    };

    const addToRecents = (note) => {
        try {
            const recents = JSON.parse(localStorage.getItem('recent_notes') || '[]');
            const filtered = recents.filter(r => r.id !== note.id);
            const newEntry = {
                id: note.id,
                title: note.title,
                icon: note.icon,
                updated_at: new Date().toISOString()
            };
            const updated = [newEntry, ...filtered].slice(0, 10);
            localStorage.setItem('recent_notes', JSON.stringify(updated));
        } catch (e) {
            console.error('Failed to update recents', e);
        }
    };

    const loadNote = async (id) => {
        loadAbortRef.current?.abort();
        const controller = new AbortController();
        loadAbortRef.current = controller;
        const gen = ++loadGenRef.current;

        setLoading(true);
        setNote(null);
        try {
            const data = await notesAPI.getNote(id, controller.signal);
            if (gen !== loadGenRef.current) return;
            setNote(data);
            addToRecents(data);
        } catch (error) {
            if (error?.name === 'AbortError') return;
            console.error('Note loading failed', error);
            if (gen !== loadGenRef.current) return;
            try {
                const recents = JSON.parse(localStorage.getItem('recent_notes') || '[]');
                const filtered = recents.filter(r => r.id !== parseInt(id, 10));
                localStorage.setItem('recent_notes', JSON.stringify(filtered));
            } catch (e) {
                console.error('Failed to clean recents', e);
            }
            setNote(null);
        } finally {
            if (gen === loadGenRef.current) setLoading(false);
        }
    };

    const saveNoteImmediate = useCallback(async (updates) => {
        const targetId = noteIdRef.current;
        if (!targetId) return;
        setSaveStatus('saving');
        try {
            const updatedNote = await notesAPI.updateNote(targetId, updates);
            if (noteIdRef.current !== targetId) return;
            if (updatedNote && typeof updatedNote === 'object') {
                // shared_with ve shared_with_details autosave yanıtından alınmaz —
                // bu alanlar share modal tarafından ayrıca güncellenir; burada
                // üzerine yazarsak race condition ile paylaşım listesi sıfırlanabilir.
                const { shared_with, shared_with_details, ...safeFields } = updatedNote;
                setNote(prev => (prev ? { ...prev, ...safeFields } : prev));
            }
            setSaveStatus('saved');
        } catch (error) {
            console.error('Save failed', error);
            setSaveStatus('error');
        }
    }, []);

    const scheduleDebouncedSave = useCallback((updates) => {
        pendingSaveRef.current = updates;
        if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
        saveDebounceRef.current = setTimeout(() => {
            saveDebounceRef.current = null;
            pendingSaveRef.current = null;
            saveNoteImmediate(updates);
        }, 1000);
    }, [saveNoteImmediate]);

    useEffect(() => {
        return () => {
            if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
        };
    }, []);

    const handleTitleChange = (e) => {
        const newTitle = e.target.value;
        setNote(prev => ({ ...prev, title: newTitle }));
        scheduleDebouncedSave({ title: newTitle });
    };

    const handleIconChange = () => {
        const newIcon = window.prompt('Emoji girin:', note.icon || '📄');
        if (newIcon) {
            setNote(prev => ({ ...prev, icon: newIcon }));
            saveNoteImmediate({ icon: newIcon });
        }
    };

    const handleEditorChange = (data) => {
        scheduleDebouncedSave({ content: data });
    };

    useEffect(() => {
        const handleSelectionChange = () => {
            const selectedText = window.getSelection()?.toString()?.trim() || '';
            if (!selectedText || !pageContainerRef.current) {
                setSelectionAction(prev => ({ ...prev, visible: false, text: '' }));
                return;
            }

            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0) {
                setSelectionAction(prev => ({ ...prev, visible: false, text: '' }));
                return;
            }

            const range = selection.getRangeAt(0);
            const ancestorNode = range.commonAncestorContainer.nodeType === Node.TEXT_NODE
                ? range.commonAncestorContainer.parentNode
                : range.commonAncestorContainer;

            if (!pageContainerRef.current.contains(ancestorNode)) {
                setSelectionAction(prev => ({ ...prev, visible: false, text: '' }));
                return;
            }

            const rect = range.getBoundingClientRect();
            if (!rect || (rect.width === 0 && rect.height === 0)) {
                setSelectionAction(prev => ({ ...prev, visible: false, text: '' }));
                return;
            }

            setSelectionAction({
                visible: true,
                text: selectedText,
                x: rect.left + rect.width / 2,
                y: rect.top - 10,
            });
        };

        document.addEventListener('selectionchange', handleSelectionChange);
        return () => {
            document.removeEventListener('selectionchange', handleSelectionChange);
        };
    }, []);

    useEffect(() => {
        setActionsState({
            hasActiveNote: Boolean(noteId && note),
            importingWord,
            exportingWord,
            isOwner: Boolean(isOwner),
            shareCount: note?.shared_with?.length || 0,
            onIconEdit: handleIconChange,
            onWordImport: () => fileInputRef.current?.click(),
            onWordExport: handleWordExport,
            onShare: openShareModal,
            onCreateTicket: () => handleCreateTicket(),
        });

        return () => {
            setActionsState({
                hasActiveNote: false,
                importingWord: false,
                exportingWord: false,
                isOwner: false,
                shareCount: 0,
                onIconEdit: null,
                onWordImport: null,
                onWordExport: null,
                onShare: null,
                onCreateTicket: null,
            });
        };
    }, [noteId, note, importingWord, exportingWord, isOwner, setActionsState]);

    if (!noteId) {
        return <NotesHome />;
    }

    if (loading) return <Loading />;
    if (!note) return <div className="p-8 text-center text-red-500">Not bulunamadı</div>;

    return (
        <div ref={pageContainerRef} className="max-w-4xl mx-auto px-4 py-6 sm:px-6 sm:py-8 md:px-10 lg:px-16 min-h-full">
            {/* Cover Image Placeholder - Notion Style */}
            <div className="group relative h-40 -mt-12 -mx-16 mb-8 bg-gradient-to-r from-pink-50 to-blue-50 dark:from-gray-800 dark:to-gray-900 overflow-hidden hidden">
                {/* Gelecekte Kapak Resmi Eklenebilir */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <button className="bg-white/80 dark:bg-black/50 px-3 py-1 rounded text-sm backdrop-blur-sm">
                        <PhotoIcon className="w-4 h-4 inline mr-1" />
                        Kapak Ekle
                    </button>
                </div>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept=".docx"
                onChange={handleWordImport}
                className="hidden"
            />

            <div className="sticky top-0 z-20 -mx-4 px-4 py-2 sm:static sm:mx-0 sm:px-0 sm:py-0 bg-white/95 dark:bg-gray-900/95 backdrop-blur border-b border-gray-100 dark:border-gray-800 sm:border-0 mb-4 flex items-center justify-end text-gray-500">
                <span className={`text-xs transition-opacity duration-150 ${
                    saveStatus === 'saved'
                        ? 'invisible'
                        : saveStatus === 'error'
                            ? 'text-red-500'
                            : 'text-gray-400'
                }`}>
                    {saveStatus === 'saving' ? 'Kaydediliyor...' : 'Kaydetme hatası'}
                </span>
            </div>

            {/* Share Modal */}
            {isShareModalOpen && createPortal(
                <div className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="w-full max-w-md h-[92dvh] sm:h-auto sm:max-h-[80vh] bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in duration-200 flex flex-col">
                        <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50/50 dark:bg-gray-900/50">
                            <h3 className="font-bold text-gray-900 dark:text-white flex items-center">
                                <UserPlusIcon className="w-5 h-5 mr-2 text-blue-500" />
                                Notu Paylaş
                            </h3>
                            <button onClick={() => setIsShareModalOpen(false)} className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors">
                                <XMarkIcon className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="p-4 flex-1 overflow-y-auto space-y-4">
                            <div className="relative mb-4">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="İsim, soyisim veya e-posta ile ara..."
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-200 dark:border-gray-700 rounded-xl leading-5 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm transition-all"
                                    value={userSearchQuery}
                                    onChange={(e) => setUserSearchQuery(e.target.value)}
                                />
                            </div>

                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">TÜM KULLANICILAR</p>

                            <div className="space-y-1">
                                {allUsers
                                    .filter(u => {
                                        const search = userSearchQuery.toLowerCase();
                                        return (
                                            u.full_name?.toLowerCase().includes(search) ||
                                            u.email?.toLowerCase().includes(search) ||
                                            u.first_name?.toLowerCase().includes(search) ||
                                            u.last_name?.toLowerCase().includes(search)
                                        );
                                    })
                                    .map(u => {
                                        const isShared = note.shared_with?.includes(u.id);
                                        return (
                                            <div key={u.id} className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-900/50 rounded-xl transition-colors group">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-xs">
                                                        {u.full_name?.charAt(0) || u.email?.charAt(0)}
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{u.full_name}</p>
                                                        <p className="text-[10px] text-gray-500">{u.email}</p>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={async () => {
                                                        setSharingLoading(true);
                                                        try {
                                                            const newSharedWith = isShared
                                                                ? note.shared_with.filter(id => id !== u.id)
                                                                : [...(note.shared_with || []), u.id];

                                                            const updatedNote = await notesAPI.updateNote(noteIdRef.current, { shared_with: newSharedWith });
                                                            setNote(updatedNote);
                                                        } catch (e) { console.error('Share toggle failed', e); }
                                                        finally { setSharingLoading(false); }
                                                    }}
                                                    disabled={sharingLoading}
                                                    className={`px-3 py-1 rounded-full text-[10px] font-bold transition-all ${isShared
                                                        ? 'bg-blue-100 text-blue-700 hover:bg-red-50 hover:text-red-700'
                                                        : 'bg-gray-100 text-gray-600 hover:bg-blue-600 hover:text-white'
                                                        } disabled:opacity-60`}
                                                >
                                                    {isShared ? 'Erişimi Kaldır' : 'Paylaş'}
                                                </button>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>

                        <div className="p-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 flex justify-end">
                            <button
                                onClick={() => setIsShareModalOpen(false)}
                                className="px-6 py-2 bg-blue-600 text-white rounded-xl font-bold text-xs hover:bg-blue-700 transition-all shadow-md active:scale-95"
                            >
                                Tamam
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Title & Icon */}
            <div className="mb-8">
                {note.icon && (
                    <div
                        className="text-4xl sm:text-6xl mb-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 w-fit p-2 rounded-lg transition-colors"
                        onClick={handleIconChange}
                    >
                        {note.icon}
                    </div>
                )}
                <input
                    type="text"
                    value={note.title}
                    onChange={handleTitleChange}
                    placeholder="Başlıksız"
                    className="w-full text-2xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 placeholder-gray-300 bg-transparent border-none focus:ring-0 px-0 py-2"
                />
            </div>

            {/* Editor - Key ile yeniden yaratılmasını sağlıyoruz sayfa değişince */}
            <div className="min-h-[360px] sm:min-h-[500px]">
                <NotesEditor
                    key={`${note.id}-${editorKey}`}
                    data={note.content}
                    onChange={handleEditorChange}
                    mentionUsers={allUsers}
                />
            </div>

            {/* Comments */}
            <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-800">
                <div className="flex items-center mb-4">
                    <ChatBubbleLeftEllipsisIcon className="w-5 h-5 mr-2 text-gray-500" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Yorumlar ({comments.length})
                    </h3>
                </div>

                <form onSubmit={handleAddComment} className="mb-5">
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-start gap-2">
                        <textarea
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            placeholder="Yorum yaz... (Kişi etiketlemek için @kullanici)"
                            className="flex-1 min-h-[96px] sm:min-h-[80px] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <button
                            type="submit"
                            disabled={sendingComment || !commentText.trim()}
                            className="inline-flex items-center justify-center px-3 py-3 sm:py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-50 hover:bg-blue-700 transition-colors"
                        >
                            <PaperAirplaneIcon className="w-4 h-4 mr-1" />
                            Gönder
                        </button>
                    </div>
                </form>

                {commentsLoading ? (
                    <div className="text-sm text-gray-500">Yorumlar yükleniyor...</div>
                ) : comments.length === 0 ? (
                    <div className="text-sm text-gray-500">Henüz yorum yok.</div>
                ) : (
                    <div className="space-y-3">
                        {comments.map((comment) => {
                            const canDelete = currentUser?.id === comment.author || currentUser?.id === note.owner || isNotesAdmin(currentUser);
                            return (
                                <div
                                    key={comment.id}
                                    className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white/70 dark:bg-gray-900/70 p-3"
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                                {comment.author_details?.full_name || 'Kullanıcı'}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {formatCommentDate(comment.created_at)}
                                            </div>
                                        </div>
                                        {canDelete && (
                                            <button
                                                onClick={() => handleDeleteComment(comment.id)}
                                                className="p-1 text-gray-400 hover:text-red-600"
                                                title="Yorumu sil"
                                            >
                                                <TrashIcon className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                    <div className="mt-2 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words">
                                        {comment.comment_text}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {selectionAction.visible && createPortal(
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleCreateTicket(selectionAction.text)}
                    className="fixed z-[950] -translate-x-1/2 -translate-y-full inline-flex items-center px-3 py-2 rounded-lg bg-green-600 text-white text-xs font-semibold shadow-lg hover:bg-green-700"
                    style={{ left: `${selectionAction.x}px`, top: `${selectionAction.y}px` }}
                >
                    <BriefcaseIcon className="w-4 h-4 mr-1.5" />
                    Seçili Metinden Talep
                </button>,
                document.body
            )}
        </div>
    );
};

export default NotesPage;
